import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Fuel, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function InviteAccept() {
  const { token } = useParams();
  const { acceptInvite, formatApiError: fmt } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invitations/${token}`);
        setPreview(data);
        setName(data.name || "");
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await acceptInvite({ token, password, name });
      toast.success("Welcome aboard!");
      navigate("/");
    } catch (e2) {
      const msg = fmt(e2);
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white"><Loader2 className="animate-spin" /></div>;

  if (!preview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-6">
        <div className="max-w-md text-center">
          <p className="ft-overline text-red-400 mb-3">INVITATION INVALID</p>
          <h1 className="font-display text-3xl font-black tracking-tighter mb-3">Sorry — this invitation has expired or been cancelled.</h1>
          <p className="text-[#A3A3A3] mb-6">{err}</p>
          <Link to="/signup" className="text-[#FF5A1F] hover:underline">Create your own account instead →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain">
      <div className="max-w-md mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#FF5A1F] flex items-center justify-center">
            <Fuel size={22} strokeWidth={2.5} className="text-black" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-lg font-black tracking-tighter">FUELTRACK</span>
            <span className="ft-overline text-[9px]">Command · Analyse · Optimise</span>
          </div>
        </div>
        <p className="ft-overline mb-2">YOU'RE INVITED</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-none mb-3">
          Join <span className="text-[#FF5A1F]">{preview.account_name || "the garage"}</span>
        </h1>
        <p className="text-[#A3A3A3] mb-8">
          As <span className="text-white font-semibold">{preview.role}</span> · {preview.email}
        </p>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="invite-form">
          <div>
            <label className="ft-overline block mb-2">Your name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] focus:ring-1 focus:ring-[#FF5A1F] transition-colors"
              data-testid="invite-name"
            />
          </div>
          <div>
            <label className="ft-overline block mb-2">Set a password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="At least 6 characters"
              className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] focus:ring-1 focus:ring-[#FF5A1F] transition-colors"
              data-testid="invite-password"
            />
          </div>
          {err && (
            <div className="text-[#EF4444] text-sm border border-[#EF4444]/30 bg-[#EF4444]/10 p-3">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#FF5A1F] text-black font-bold px-6 py-3 hover:bg-[#E04F1A] transition-colors duration-200 disabled:opacity-60 inline-flex items-center justify-center gap-2"
            data-testid="invite-submit"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            Accept invitation
          </button>
        </form>
      </div>
    </div>
  );
}
